import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  DashboardOutlined,
  PeopleOutlined,
  BookOnlineOutlined,
  AccountBalanceOutlined,
  FeedbackOutlined,
  SupportAgentOutlined,
  LogoutOutlined,
  PersonOutlined,
  DarkModeOutlined,
  LightModeOutlined,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  PrivacyTipOutlined,
  SettingsOutlined,
  Notifications,
  Analytics,
  FileDownload,
  SupervisorAccount,
  PaymentsOutlined,
} from '@mui/icons-material';
import { Chip } from '@mui/material';
import { authService } from '../services/auth.service';
import { environmentService } from '../config/environment';
import { ColorModeContext } from '../App';
import logoLight from '../assets/logos/logo_light.png';
import logoDark from '../assets/logos/logo_dark.png';

const drawerWidth = 240;
const collapsedDrawerWidth = 88;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const user = authService.getCurrentUser();

  const currentDrawerWidth = isCollapsed ? collapsedDrawerWidth : drawerWidth;
  const userRole = user?.role || 'support';

  const menuItems = [
    { text: 'Privacy Policy', icon: <PrivacyTipOutlined />, path: '/privacy-policy', public: true, allowedRoles: ['super_admin', 'admin', 'support'] },
  ];

  const authenticatedMenuItems = [
    { text: 'Dashboard', icon: <DashboardOutlined />, path: '/dashboard', allowedRoles: ['super_admin', 'admin', 'support'] },
    { text: 'Users', icon: <PeopleOutlined />, path: '/users', allowedRoles: ['super_admin', 'admin', 'support'] },
    { text: 'Bookings', icon: <BookOnlineOutlined />, path: '/bookings', allowedRoles: ['super_admin', 'admin', 'support'] },
    { text: 'Financial', icon: <AccountBalanceOutlined />, path: '/financial', allowedRoles: ['super_admin'] },
    { text: 'Payouts', icon: <PaymentsOutlined />, path: '/payouts', allowedRoles: ['super_admin'] },
    { text: 'Feedback', icon: <FeedbackOutlined />, path: '/feedback', allowedRoles: ['super_admin', 'admin', 'support'] },
    { text: 'Tickets', icon: <SupportAgentOutlined />, path: '/tickets', allowedRoles: ['super_admin', 'admin', 'support'] },
    { text: 'Notifications', icon: <Notifications />, path: '/notifications', allowedRoles: ['super_admin', 'admin'] },
    { text: 'Analytics', icon: <Analytics />, path: '/analytics', allowedRoles: ['super_admin', 'admin'] },
    { text: 'Reports', icon: <FileDownload />, path: '/reports', allowedRoles: ['super_admin', 'admin'] },
    { text: 'Admins', icon: <SupervisorAccount />, path: '/admins', allowedRoles: ['super_admin'] },
    { text: 'Settings', icon: <SettingsOutlined />, path: '/settings', allowedRoles: ['super_admin'] },
    ...menuItems
  ];

  const displayMenuItems = (authService.isAuthenticated() ? authenticatedMenuItems : menuItems)
    .filter(item => item.allowedRoles.includes(userRole));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    setAnchorEl(null);
    await authService.logout();
    navigate('/login', { replace: true });
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ p: isCollapsed ? 0.5 : 1, display: 'flex', alignItems: 'center', transition: 'padding 0.3s', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
        <Box
          component="img"
          src={theme.palette.mode === 'light' ? logoLight : logoDark}
          alt="Teachora Logo"
          sx={{
            height: isCollapsed ? 40 : 150,
            width: 'auto',
            transition: 'all 0.3s',
            objectFit: 'contain'
          }}
        />
      </Box>
      <Box sx={{ px: 2, pb: 2 }}>
        <Chip
          label={environmentService.getCurrentEnvironment().toUpperCase()}
          color={environmentService.getCurrentEnvironment() === 'prod' ? 'error' : 'primary'}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 700, fontSize: '0.625rem', height: 20 }}
        />
      </Box>
      <Divider sx={{ mx: 2, mb: 1, opacity: 0.5 }} />
      <List
        sx={{
          px: isCollapsed ? 1 : 1,
          flexGrow: 1,
          transition: 'padding 0.3s',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'light'
              ? 'rgba(0, 0, 0, 0.2)'
              : 'rgba(255, 255, 255, 0.2)',
            borderRadius: '3px',
            '&:hover': {
              background: theme.palette.mode === 'light'
                ? 'rgba(0, 0, 0, 0.3)'
                : 'rgba(255, 255, 255, 0.3)',
            },
          },
        }}
      >
        {displayMenuItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <MuiTooltip title={isCollapsed ? item.text : ''} placement="right">
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    py: 1.25,
                    px: isCollapsed ? 1.5 : 1.5,
                    backgroundColor: active ? 'primary.main' : 'transparent',
                    color: active ? 'primary.contrastText' : 'text.secondary',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    '&:hover': {
                      backgroundColor: active ? 'primary.dark' : 'action.hover',
                      color: active ? 'primary.contrastText' : 'text.primary',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: active ? 'inherit' : 'inherit',
                      minWidth: isCollapsed ? 0 : 40,
                      mr: isCollapsed ? 0 : 0,
                      justifyContent: 'center'
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: active ? 600 : 500
                      }}
                    />
                  )}
                </ListItemButton>
              </MuiTooltip>
            </ListItem>
          );
        })}
      </List>

      {user && (
        <Box sx={{ p: isCollapsed ? 1 : 2, mt: 'auto', transition: 'padding 0.3s' }}>
          <Box
            sx={{
              p: isCollapsed ? 1 : 2,
              borderRadius: 3,
              bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'rgba(255,255,255,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              gap: isCollapsed ? 0 : 1.5,
              transition: 'all 0.3s'
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              <PersonOutlined fontSize="small" />
            </Avatar>
            {!isCollapsed && (
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {user?.name || 'Admin'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                  {user?.email || 'admin@teachora.com'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { lg: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { lg: `${currentDrawerWidth}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 64, sm: 72 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { lg: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={() => setIsCollapsed(!isCollapsed)}
              sx={{ mr: 2, display: { xs: 'none', lg: 'inline-flex' }, border: '1px solid', borderColor: 'divider' }}
            >
              {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', display: { xs: 'none', sm: 'block' } }}>
              {displayMenuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <MuiTooltip title={`Switch to ${theme.palette.mode === 'light' ? 'dark' : 'light'} mode`}>
              <IconButton onClick={colorMode.toggleColorMode} color="inherit" sx={{ border: '1px solid', borderColor: 'divider' }}>
                {theme.palette.mode === 'dark' ? <LightModeOutlined /> : <DarkModeOutlined />}
              </IconButton>
            </MuiTooltip>

            {user ? (
              <>
                <IconButton onClick={handleMenuClick} sx={{ p: 0.5, border: '1px solid', borderColor: 'divider' }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                    {user?.name?.charAt(0) || 'A'}
                  </Avatar>
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      minWidth: 180,
                      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                    }
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
                    <ListItemIcon>
                      <LogoutOutlined fontSize="small" color="error" />
                    </ListItemIcon>
                    <Typography variant="body2" fontWeight={600}>Logout</Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <IconButton
                onClick={() => navigate('/login')}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                Login
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { lg: currentDrawerWidth },
          flexShrink: { lg: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          width: { lg: `calc(100% - ${currentDrawerWidth}px)` },
          mt: { xs: 8, sm: 9 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;

